import React from 'react';
import {
  Box,
  Paper,
  Typography,
  List,
  ListItem,
  ListItemText,
  ListItemSecondaryAction,
  IconButton,
  Chip,
  LinearProgress,
  useTheme
} from '@mui/material';
import {
  Delete as DeleteIcon,
  CloudUpload as CloudUploadIcon,
  CheckCircle as CheckCircleIcon,
  Error as ErrorIcon,
  Pending as PendingIcon
} from '@mui/icons-material';
import { motion, AnimatePresence } from 'framer-motion';

export interface QueuedFile {
  id: string;
  file: File;
  status: 'pending' | 'processing' | 'completed' | 'error';
  progress: number;
  error?: string;
  result?: string;
}

interface FileQueueProps {
  files: QueuedFile[];
  onRemoveFile: (id: string) => void;
  onClearAll: () => void;
}

const FileQueue: React.FC<FileQueueProps> = ({ files, onRemoveFile, onClearAll }) => {
  const theme = useTheme();

  const getStatusIcon = (status: QueuedFile['status']) => {
    switch (status) {
      case 'pending':
        return <PendingIcon color="action" />;
      case 'processing':
        return <CloudUploadIcon color="primary" />;
      case 'completed':
        return <CheckCircleIcon color="success" />;
      case 'error':
        return <ErrorIcon color="error" />;
      default:
        return <PendingIcon color="action" />;
    }
  };

  const getStatusColor = (status: QueuedFile['status']) => {
    switch (status) {
      case 'pending':
        return 'default';
      case 'processing':
        return 'primary';
      case 'completed':
        return 'success';
      case 'error':
        return 'error';
      default:
        return 'default';
    }
  };

  const getStatusText = (status: QueuedFile['status']) => {
    switch (status) {
      case 'pending':
        return 'Pending';
      case 'processing':
        return 'Processing';
      case 'completed':
        return 'Completed';
      case 'error':
        return 'Error';
      default:
        return 'Unknown';
    }
  };

  if (files.length === 0) {
    return null;
  }

  return (
    <motion.div
      initial={{ opacity: 0, y: 20 }}
      animate={{ opacity: 1, y: 0 }}
      transition={{ duration: 0.3 }}
    >
      <Paper sx={{ p: 2, mt: 2 }}>
        <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 2 }}>
          <Typography variant="h6" color="text.primary">
            File Queue ({files.length})
          </Typography>
          <IconButton 
            onClick={onClearAll}
            size="small"
            color="error"
            title="Clear all files"
          >
            <DeleteIcon />
          </IconButton>
        </Box>
        
        <List sx={{ p: 0 }}>
          <AnimatePresence>
            {files.map((queuedFile) => (
              <motion.div
                key={queuedFile.id}
                initial={{ opacity: 0, x: -20 }}
                animate={{ opacity: 1, x: 0 }}
                exit={{ opacity: 0, x: 20 }}
                transition={{ duration: 0.2 }}
              >
                <ListItem
                  sx={{
                    border: `1px solid ${theme.palette.divider}`,
                    borderRadius: 1,
                    mb: 1,
                    backgroundColor: queuedFile.status === 'processing' ? 'action.hover' : 'background.paper'
                  }}
                >
                  <Box sx={{ display: 'flex', alignItems: 'center', mr: 2 }}>
                    {getStatusIcon(queuedFile.status)}
                  </Box>
                  
                  <ListItemText
                    primary={
                      <Typography variant="body2" noWrap sx={{ maxWidth: 200 }}>
                        {queuedFile.file.name}
                      </Typography>
                    }
                    secondary={
                      <Box sx={{ width: '100%' }}>
                        <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 1 }}>
                          <Chip
                            label={getStatusText(queuedFile.status)}
                            color={getStatusColor(queuedFile.status)}
                            size="small"
                            variant="outlined"
                          />
                          <Typography variant="caption" color="text.secondary">
                            {(queuedFile.file.size / 1024 / 1024).toFixed(2)} MB
                          </Typography>
                        </Box>
                        
                        {queuedFile.status === 'processing' && (
                          <LinearProgress 
                            variant="determinate" 
                            value={queuedFile.progress} 
                            sx={{ height: 4, borderRadius: 2 }}
                          />
                        )}
                        
                        {queuedFile.error && (
                          <Typography variant="caption" color="error" sx={{ mt: 1, display: 'block' }}>
                            {queuedFile.error}
                          </Typography>
                        )}
                      </Box>
                    }
                  />
                  
                  <ListItemSecondaryAction>
                    <IconButton
                      edge="end"
                      onClick={() => onRemoveFile(queuedFile.id)}
                      disabled={queuedFile.status === 'processing'}
                      size="small"
                      color="error"
                      title="Remove file"
                    >
                      <DeleteIcon />
                    </IconButton>
                  </ListItemSecondaryAction>
                </ListItem>
              </motion.div>
            ))}
          </AnimatePresence>
        </List>
      </Paper>
    </motion.div>
  );
};

export default FileQueue;
